// „Целият списък" — целият каталог в модал: филтър по няколко думи (име +
// вторичния текст), групи (релса на десктоп, чипове под десктоп) с броячи,
// превключватели „Само с наличност" / „Под минимума" / „От този доставчик" /
// „Скрий добавените", отметки на много редове наведнъж и лента с избраните.
// Общ за Доставка, Изписване, Прехвърляне, Взаимозаменяеми и Инвентаризация —
// всеки лист подава своите редове (колоните или заварения метатекст) и кое
// вече е вътре. С `entry` отметнатият ред носи полета (количество; при
// доставка и ед. цена + сума) и „Добави" подава въведеното на листа.
//
// Размер: на десктоп почти цял екран — min(1440px, 96vw) × 90vh, ФИКСИРАНО
// (модалът не подскача, докато се филтрира); на телефон е лист на цял екран.
//
// Клавиатура — фокусът живее във филтъра:
//   ↑/↓ ред · PageUp/PageDown страница · Ctrl/Cmd+Home/End първи/последен;
//   Enter отмята маркирания ред (Space пише интервал — търсенето е по думи);
//     с `entry` Enter отмята И слага курсора в количеството, Shift+Enter само
//     отмята/маха; в поле: Tab → цената, Enter → следващият ред;
//     при празен списък Enter показва всички групи (ако има съвпадения
//     другаде) или създава „+ Нов продукт „…"" (ако листът подава onCreate);
//   Ctrl/Cmd+Enter добавя отметнатите (отвсякъде); няма ли отметнати —
//     маркирания ред;
//   Alt+↑/↓ сменя групата, без да мести фокуса; Tab стига до релсата;
//   Esc: текст във филтъра → чисти го; иначе затваря; извън филтъра (поле,
//     релса) → връща фокуса в него. Всеки Esc handler вика preventDefault.
// antd Modal → общият пазач за отворен слой (hasOpenOverlay) го вижда.
import { useId, useRef } from "react";
import { Modal } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { useViewport } from "../../../../hooks/useViewport";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { CheckBox } from "../filters/CheckBox";
import { KbdChip } from "../KbdChip";
import type { CatalogItem } from "./catalogPickerModel";
import type { CatalogEntryConfig, CatalogEntryValues } from "./catalogEntryModel";
import { catalogOptionId } from "./catalogPickerLayout";
import { useCatalogPicker } from "./useCatalogPicker";
import { CatalogGroupRail } from "./CatalogGroupRail";
import { CatalogPickerList } from "./CatalogPickerList";
import { CatalogSelectionTray } from "./CatalogSelectionTray";

export type { CatalogItem, CatalogStockTone } from "./catalogPickerModel";
export type { CatalogEntryConfig, CatalogEntryValue, CatalogEntryValues } from "./catalogEntryModel";

type Props = {
  open: boolean;
  title?: string;
  // подсказката под заглавието (по подразбиране — текстът на доставката)
  hint?: string;
  items: CatalogItem[];
  onClose: () => void;
  // id-тата в реда на каталога; листът сам затваря модала. С `entry` идва и
  // въведеното (само редовете с нещо написано; вече попълнен ред в листа е
  // в него само след потвърден презапис) — без `entry` вторият аргумент липсва.
  onAdd: (ids: number[], values?: CatalogEntryValues) => void;
  // въвеждането в реда: какви полета носи отметнатият ред
  entry?: CatalogEntryConfig;
  // вече добавените (added) могат да се отметнат — напр. Инвентаризация ги
  // ползва за скок до реда; по подразбиране само се виждат
  addedSelectable?: boolean;
  // „+ Нов продукт „{текст}"" в празното състояние и във футъра; получава
  // написаното (trim). Листът решава дали да затвори модала.
  onCreate?: (query: string) => void;
  // заглавието на колоната с цената (по подразбиране „Цена")
  priceLabel?: string;
  // името на избрания доставчик — в подсказката на чипа „От този доставчик"
  // (чипът се появява, когато има редове с `fromSupplier`)
  supplierName?: string | null;
  // след анимацията на затваряне; added — в това отваряне е викан onAdd
  // (затворен след „Добави", не с Отказ/Esc/×).
  // antd НЕ връща фокуса на бутона „Целият списък" (focusTriggerAfterClose=false):
  // фокусът е на листа — onAdd слага курсора в добавения ред, а тук листът
  // може да върне фокуса в търсачката след Отказ/Esc.
  onAfterClose?: (added: boolean) => void;
};

// Телефон: лист на цял екран — без радиус и сянка. Това са вътрешните
// елементи на antd Modal (content), недостижими за Tailwind → styles обект.
const SHEET_MODAL_STYLES = {
  ...modalStyles,
  content: { ...modalStyles.content, borderRadius: 0, boxShadow: "none" },
};

const FOCUS_RING = "outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]";

export const CatalogPickerModal = (props: Props) => {
  const { isNarrow } = useInventoryShell();
  // Затворен ли е с „Добави" — за onAfterClose
  const addedRef = useRef(false);
  // без `entry` листът получава само id-тата (заварените викачи)
  const onAdd = (ids: number[], values?: CatalogEntryValues) => {
    addedRef.current = true;
    if (values) props.onAdd(ids, values);
    else props.onAdd(ids);
  };
  return (
    <Modal
      open={props.open}
      onCancel={props.onClose}
      afterClose={() => {
        props.onAfterClose?.(addedRef.current);
        addedRef.current = false;
      }}
      // иначе antd връща фокуса на бутона-тригер СЛЕД анимацията и отменя
      // focusCell(...) на листа в първия добавен ред
      focusTriggerAfterClose={false}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isNarrow ? "100vw" : "min(1440px, 96vw)"}
      styles={isNarrow ? SHEET_MODAL_STYLES : modalStyles}
      style={isNarrow ? undefined : modalWrapStyle}
      className={isNarrow ? "!m-0 !max-w-[100vw] !p-0" : undefined}
    >
      <CatalogPickerBody {...props} onAdd={onAdd} isNarrow={isNarrow} />
    </Modal>
  );
};

type ToggleProps = { on: boolean; label: string; isNarrow: boolean; onToggle: () => void; title?: string };

// Превключвател-хапче („Само с наличност", „Под минимума", „Скрий добавените")
const FilterToggle = ({ on, label, isNarrow, onToggle, title }: ToggleProps) => (
  <button
    type="button"
    aria-pressed={on}
    title={title}
    onMouseDown={isNarrow ? undefined : (event) => event.preventDefault()}
    onClick={onToggle}
    className={`inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border py-[5px] pl-2 pr-3 text-[12.5px] leading-[normal] ${FOCUS_RING} ${
      on
        ? "border-brand/45 bg-brand-tint font-semibold text-brand-deep-ink"
        : "border-shade-3 bg-surface text-ink-2 hover:border-shade-5"
    }`}
  >
    <CheckBox checked={on} />
    {label}
  </button>
);

// Тялото живее вътре в Modal (destroyOnHidden) → състоянието е свежо при
// всяко отваряне, без ръчно нулиране в afterClose
const CatalogPickerBody = ({
  title,
  hint,
  items,
  onClose,
  onAdd,
  addedSelectable = false,
  onCreate,
  priceLabel,
  entry,
  supplierName,
  isNarrow,
}: Props & { isNarrow: boolean }) => {
  const { t } = useTranslation();
  const { isDesktop } = useViewport();
  const listId = useId();
  const picker = useCatalogPicker({ items, addedSelectable, isNarrow, listId, entry, onAdd, onClose, onCreate });
  const { visible, active, groups } = picker;
  const showRail = groups.length > 0;
  // Под десктоп групите са чипове над списъка — колоните искат ширината
  const groupsAsChips = isNarrow || !isDesktop;

  const toggles = (
    <>
      {picker.anyStockTone && (
        <FilterToggle
          on={picker.onlyInStock}
          label={t("inventoryPage.catOnlyInStock")}
          isNarrow={isNarrow}
          onToggle={() => picker.setOnlyInStock((value) => !value)}
        />
      )}
      {picker.anyBelowMinimum && (
        <FilterToggle
          on={picker.belowMinimum}
          label={t("inventoryPage.catBelowMinimum")}
          title={t("inventoryPage.catBelowMinimumHint")}
          isNarrow={isNarrow}
          onToggle={() => picker.setBelowMinimum((value) => !value)}
        />
      )}
      {picker.anyFromSupplier && (
        <FilterToggle
          on={picker.fromSupplier}
          label={t("inventoryPage.catFromSupplier")}
          title={supplierName ? t("inventoryPage.catFromSupplierHint", { name: supplierName }) : undefined}
          isNarrow={isNarrow}
          onToggle={() => picker.setFromSupplier((value) => !value)}
        />
      )}
      {picker.anyAdded && (
        <FilterToggle
          on={picker.hideAdded}
          label={t("inventoryPage.catHideAdded")}
          isNarrow={isNarrow}
          onToggle={() => picker.setHideAdded((value) => !value)}
        />
      )}
    </>
  );

  const rail = (
    <CatalogGroupRail
      groups={groups}
      group={picker.group}
      counts={picker.view.counts}
      total={picker.view.total}
      isNarrow={groupsAsChips}
      onChoose={picker.chooseGroup}
    />
  );

  return (
    <div
      className={`flex flex-col bg-surface ${isNarrow ? "h-dvh" : "h-[90vh] max-h-[calc(100dvh-32px)] min-h-[420px]"}`}
      onKeyDown={picker.onBodyKeyDown}
    >
      {/* Хедър: заглавие + подсказка, вдясно × */}
      <div className={`flex items-start gap-3 border-b border-shade-2 ${isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"}`}>
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="text-[17px] font-semibold leading-[1.2] text-ink">
            {title ?? t("inventoryPage.catalogTitle")}
          </span>
          <span className="text-[13px] leading-[1.4] text-body">{hint ?? t("inventoryPage.catalogHint")}</span>
        </div>
        <button
          type="button"
          aria-label={t("inventoryPage.close")}
          onClick={onClose}
          className={`flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-sm text-muted hover:bg-fill hover:text-ink ${FOCUS_RING}`}
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Филтър + превключватели */}
      <div
        className={`flex flex-wrap items-center gap-2 ${
          isNarrow ? "px-4 pb-2.5 pt-3" : "border-b border-shade-2 px-[22px] py-3"
        }`}
      >
        <div className={`relative min-w-0 ${isNarrow ? "basis-full" : "min-w-[240px] flex-1"}`}>
          <SearchOutlined
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-muted"
          />
          <input
            ref={picker.inputRef}
            autoFocus
            value={picker.query}
            onChange={(event) => picker.setQuery(event.target.value)}
            onKeyDown={picker.onInputKeyDown}
            placeholder={t("inventoryPage.catalogFilterPh")}
            aria-label={t("inventoryPage.catalogFilterPh")}
            aria-controls={listId}
            aria-activedescendant={active ? catalogOptionId(listId, active.id) : undefined}
            className={`w-full rounded-[10px] border border-shade-3 bg-surface py-[9px] pl-9 pr-3 text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
              isNarrow ? "text-base" : "text-sm"
            }`}
          />
        </div>
        {/* телефон: превключвателите на един ред, който се плъзга настрани */}
        {isNarrow ? (
          <div className="flex basis-full gap-2 overflow-x-auto [scrollbar-width:none]">{toggles}</div>
        ) : (
          toggles
        )}
      </div>
      {groupsAsChips && showRail && (
        <div className={isNarrow ? "" : "pt-2.5"}>{rail}</div>
      )}

      {/* Релса с групите + списъкът */}
      <div className={`flex min-h-0 flex-1 ${groupsAsChips ? "border-t border-shade-2" : ""}`}>
        {!groupsAsChips && showRail && rail}
        <div className="flex min-w-0 flex-1 flex-col">
          <CatalogPickerList
            picker={picker}
            listId={listId}
            isNarrow={isNarrow}
            addedSelectable={addedSelectable}
            itemCount={items.length}
            priceLabel={priceLabel}
          />
          {isDesktop && !isNarrow && (
            <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap border-t border-shade-2 bg-panel px-4 py-[7px] text-[11.5px] leading-[normal] text-muted">
              <span className="font-sheet tabular-nums">
                {t("inventoryPage.catShownOf", { shown: visible.length, total: items.length })}
              </span>
              <div className="flex-1" />
              <span className="flex items-center gap-1">
                <KbdChip>↑↓</KbdChip> {t("inventoryPage.catKeyRow")}
              </span>
              <span className="flex items-center gap-1">
                <KbdChip>Enter</KbdChip> {entry ? t("inventoryPage.catKeyEdit") : t("inventoryPage.catalogHintToggle")}
              </span>
              {entry && (
                <span className="flex items-center gap-1">
                  <KbdChip>Shift+Enter</KbdChip> {t("inventoryPage.catalogHintToggle")}
                </span>
              )}
              {entry?.kind === "qtyPrice" && (
                <span className="flex items-center gap-1">
                  <KbdChip>Tab</KbdChip> {t("inventoryPage.catKeyPrice")}
                </span>
              )}
              {showRail && (
                <span className="flex items-center gap-1">
                  <KbdChip>Alt+↑↓</KbdChip> {t("inventoryPage.catKeyGroup")}
                </span>
              )}
              <span className="flex items-center gap-1">
                <KbdChip>Esc</KbdChip>{" "}
                {picker.query ? t("inventoryPage.catKeyEscClear") : t("inventoryPage.catKeyEscClose")}
              </span>
            </div>
          )}
        </div>
      </div>

      <CatalogSelectionTray
        pickedItems={picker.pickedItems}
        entries={entry ? picker.entries : undefined}
        pickedSum={picker.pickedSum}
        isNarrow={isNarrow}
        query={picker.query}
        onUnpick={picker.unpick}
        onClear={picker.clearPicked}
        onAdd={() => void picker.add()}
        onCancel={onClose}
        onCreate={picker.create}
      />
    </div>
  );
};

export default CatalogPickerModal;
